import { useParams, useNavigate, Link } from "react-router";
import { useJoinJam } from "@/hooks/api/useJams";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

/**
 * Invite-link landing page (/join/:code). Logged-out visitors are routed
 * through login/signup by ProtectedRoute and land back here afterwards.
 */
export default function JoinJam() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const joinJam = useJoinJam();

  const handleJoin = () => {
    if (!code) return;
    joinJam.mutate(code, {
      onSuccess: (result) => {
        toast({ title: `Joined "${result.data.jam.name}"` });
        navigate(`/jams/${result.data.jam.id}`, { replace: true });
      },
    });
  };

  return (
    <div className="max-w-xl">
      <div className="bg-[#191919] border border-[#616161]/20 rounded-lg p-8 text-center">
        <h1 className="text-xl font-medium uppercase tracking-wider mb-2">
          Join Jam
        </h1>
        <p className="text-muted-foreground mb-6">
          You&apos;ve been invited to collaborate on a jam.
        </p>
        <code className="inline-block bg-[#1e1e1e] border border-[#616161]/20 rounded-md px-4 py-3 text-2xl font-mono tracking-[0.35em] mb-6">
          {code}
        </code>
        <div className="flex justify-center gap-3">
          <Button onClick={handleJoin} disabled={joinJam.isPending}>
            {joinJam.isPending ? "Joining..." : "Join Jam"}
          </Button>
          <Link to="/jams">
            <Button variant="outline">Cancel</Button>
          </Link>
        </div>
        {joinJam.isError && (
          <p className="text-sm text-destructive mt-4">
            {joinJam.error?.message === "No jam found for that code"
              ? "This invite code is invalid or has been regenerated. Ask for a new link."
              : joinJam.error?.message || "Failed to join jam"}
          </p>
        )}
      </div>
    </div>
  );
}
